import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useParams } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, Clock, Loader2, ShieldCheck,
  TrendingDown, TrendingUp,
} from 'lucide-react';
import { foldJsonInMarkdown, promoteBoldLinesToHeadings } from '../../lib/jsonFold';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const verdictTone = {
  BUY: 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40',
  ADD: 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40',
  HOLD: 'bg-slate-500/15 text-slate-300 border-slate-600/40',
  PASS: 'bg-slate-500/15 text-slate-300 border-slate-600/40',
  TRIM: 'bg-amber-500/15 text-amber-300 border-amber-700/40',
  SELL: 'bg-rose-500/15 text-rose-300 border-rose-700/40',
};

/** Public, polled view of a Health Check audit. */
export default function HealthCheckResultPage() {
  const { token } = useParams();
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);

  // Poll while running.
  useEffect(() => {
    let cancelled = false;
    const fetchView = async () => {
      try {
        const { data } = await axios.get(`${API}/healthcheck/${token}`);
        if (cancelled) return;
        setView(data);
        if (data?.status && ['pending', 'running'].includes(data.status)) {
          setTimeout(() => setTick((t) => t + 1), 4000);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || 'Failed to load audit');
      }
    };
    fetchView();
    return () => { cancelled = true; };
  }, [token, tick]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-300">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-rose-400" />
          <p className="mt-3 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-300">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  const inProgress = ['pending', 'running'].includes(view.status);
  const summary = view.summary || {};
  const verdictMix = summary.verdict_counts || {};
  const verdictMixOrder = ['BUY', 'ADD', 'HOLD', 'PASS', 'TRIM', 'SELL'];
  const total = view.holdings?.length || 0;
  const completed = (view.holdings || []).filter((h) => h.verdict).length;
  const progressPct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 text-[11px] font-medium text-emerald-300 mb-2">
              <ShieldCheck className="w-3 h-3" />
              KubAIr Health Check
            </div>
            <h1 className="text-2xl font-bold">Portfolio audit</h1>
            <p className="text-sm text-slate-400 mt-1">
              {total} holdings &middot; status: <span className="text-slate-200">{view.status}</span>
            </p>
          </div>
          {inProgress && (
            <div className="text-right">
              <div className="inline-flex items-center gap-2 text-amber-300 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Running committee on {total} stocks...
              </div>
              <div className="mt-2 w-48 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-400 transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <div className="text-xs text-slate-500 mt-1">{completed} of {total} done</div>
            </div>
          )}
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {verdictMixOrder.map((v) => (
            verdictMix[v] ? (
              <div key={v} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
                <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold border ${verdictTone[v] || ''}`}>
                  {v}
                </div>
                <div className="text-2xl font-bold mt-2">{verdictMix[v]}</div>
                <div className="text-[11px] text-slate-500">holdings</div>
              </div>
            ) : null
          ))}
        </div>

        {summary.regime_off_count !== undefined && (
          <div className="rounded-xl border border-amber-800/40 bg-amber-950/20 p-4 mb-8 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-amber-200">
                {summary.regime_off_count} of {total} holdings in RISK_OFF regime
              </div>
              <div className="text-xs text-amber-300/80 mt-1">
                Stocks below 200DMA + weakening momentum. The committee weighted valuation
                discipline higher for these.
              </div>
            </div>
          </div>
        )}

        {/* Holdings table */}
        <div className="rounded-xl border border-slate-800 overflow-hidden mb-8">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-xs text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Stock</th>
                <th className="text-left px-4 py-3 font-medium">Sector</th>
                <th className="text-left px-4 py-3 font-medium">Verdict</th>
                <th className="text-left px-4 py-3 font-medium">Conv.</th>
                <th className="text-left px-4 py-3 font-medium">Thesis</th>
              </tr>
            </thead>
            <tbody>
              {(view.holdings || []).map((h) => (
                <tr key={h.isin} className="border-t border-slate-800/60">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-100">{h.nse_symbol || h.isin}</div>
                    <div className="text-[11px] text-slate-500">{h.company_name}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{h.sector || '—'}</td>
                  <td className="px-4 py-3">
                    {h.verdict ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${verdictTone[h.verdict] || ''}`}>
                        {h.verdict}
                      </span>
                    ) : h.error ? (
                      <span className="text-rose-400 text-xs">error</span>
                    ) : (
                      <Clock className="w-3.5 h-3.5 text-slate-500" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-300 text-xs">{h.conviction ? `${h.conviction}/10` : '—'}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs max-w-md">
                    {h.one_line_thesis || (h.error ? `(error: ${h.error})` : '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Aggregated memo */}
        {view.summary_md && (
          <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              Portfolio memo
            </h2>
            <div className="prose prose-sm prose-invert max-w-none break-words leading-relaxed
                            prose-headings:font-semibold prose-headings:text-slate-100
                            prose-h1:text-base prose-h2:text-sm prose-h3:text-sm
                            prose-h2:mt-5 prose-h2:mb-2 prose-h2:text-emerald-300
                            prose-h3:mt-4 prose-h3:mb-1 prose-h3:uppercase prose-h3:tracking-wide prose-h3:text-slate-400
                            prose-p:my-3 prose-ul:my-3 prose-ul:pl-5 prose-li:my-1
                            prose-strong:text-slate-100
                            prose-pre:max-w-full prose-pre:overflow-x-auto prose-pre:whitespace-pre-wrap
                            prose-pre:break-words prose-pre:text-xs prose-pre:bg-slate-900/60">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {promoteBoldLinesToHeadings(foldJsonInMarkdown(view.summary_md))}
              </ReactMarkdown>
            </div>
          </div>
        )}

        {/* CTA */}
        {view.status === 'completed' && (
          <div className="mt-8 rounded-xl border border-emerald-800/40 bg-emerald-950/20 p-6 text-center">
            <h3 className="text-lg font-semibold text-emerald-200">Want this every night?</h3>
            <p className="text-sm text-emerald-300/80 mt-1">
              Subscribe to Protect — the committee re-runs nightly on your holdings and alerts
              you the moment any verdict moves.
            </p>
            <a
              href="/login"
              className="inline-block mt-4 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold px-6 py-2 text-sm"
            >
              Set up Protect
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
